import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Build as ProductionIcon,
  CheckCircle as PostIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  WarningAmber as WarnIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import productionOrderService from '../../../../services/productionOrderService';
import venueLocationService from '../../../../services/venueLocationService';
import ownershipAccountService from '../../../../services/ownershipAccountService';
import inventoryItemService from '../../../../services/inventoryItemService';
import inventoryLotService from '../../../../services/inventoryLotService';
import recipeService from '../../../../services/recipeService';
import type {
  ProductionOrder,
  ProductionOrderLine,
  ProductionOrderAvailability,
  ProductionLineAvailability,
  ProductionPostingResult,
  VenueLocation,
  OwnershipAccount,
  ItemLotsResponse,
} from '../../../../types/venue';
import type { UpdateProductionOrderLinePayload } from '../../../../services/productionOrderService';
import { formatQty } from '../../../../utils/quantityFormat';

// ── Status badge ───────────────────────────────────────────────────────────────

const ProductionStatusBadge: React.FC<{ status: 'DRAFT' | 'POSTED' }> = ({ status }) => {
  const cfg = status === 'POSTED'
    ? { label: 'Posted', color: '#1a7f37', bg: '#dafbe1' }
    : { label: 'Draft', color: '#9a6700', bg: '#fef9c3' };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ fontWeight: 600, fontSize: '0.7rem', color: cfg.color, backgroundColor: cfg.bg, border: 'none', height: 22 }}
    />
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const isoToDateInput = (iso?: string | null) =>
  iso ? new Date(iso).toISOString().slice(0, 10) : '';


// ── Main component ─────────────────────────────────────────────────────────────

const ProductionOrderDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId } = useVenue();
  const { orderId } = useParams<{ orderId: string }>();
  const backPath = `/admin/venues/${venueId}/production-orders`;

  // ── Core data ──────────────────────────────────────────────────────────────
  const [order, setOrder] = useState<ProductionOrder | null>(null);
  const [lines, setLines] = useState<ProductionOrderLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // ── Reference data ─────────────────────────────────────────────────────────
  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [accounts, setAccounts] = useState<OwnershipAccount[]>([]);
  const [recipeName, setRecipeName] = useState('');
  const [outputItemName, setOutputItemName] = useState('');

  // ── Header edit ────────────────────────────────────────────────────────────
  const [headerEditing, setHeaderEditing] = useState(false);
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [editQty, setEditQty] = useState('');
  const [editLocId, setEditLocId] = useState('');
  const [editAcctId, setEditAcctId] = useState('');
  const [editPlannedDate, setEditPlannedDate] = useState('');
  const [editExpiryDate, setEditExpiryDate] = useState('');
  const [editLotNumber, setEditLotNumber] = useState('');
  const [editNotes, setEditNotes] = useState('');

  // ── Availability ───────────────────────────────────────────────────────────
  const [availability, setAvailability] = useState<ProductionOrderAvailability | null>(null);
  const [avLoading, setAvLoading] = useState(false);
  const [avError, setAvError] = useState<string | null>(null);

  const avMap = useMemo((): Map<string, ProductionLineAvailability> => {
    if (!availability) return new Map();
    return new Map(availability.lines.map(al => [al.productionOrderLineId, al]));
  }, [availability]);

  // ── Line edit dialog ───────────────────────────────────────────────────────
  const [editLineOpen, setEditLineOpen] = useState(false);
  const [editingLine, setEditingLine] = useState<ProductionOrderLine | null>(null);
  const [lineLotsMap, setLineLotsMap] = useState<Map<string, ItemLotsResponse>>(new Map());
  const [lineLotsLoading, setLineLotsLoading] = useState(false);
  const [lineFromLoc, setLineFromLoc] = useState('');
  const [lineOwnership, setLineOwnership] = useState('');
  const [lineLotId, setLineLotId] = useState('');
  const [lineSaving, setLineSaving] = useState(false);
  const [lineError, setLineError] = useState<string | null>(null);

  // ── Posting ────────────────────────────────────────────────────────────────
  const [driftConfirmOpen, setDriftConfirmOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [postResult, setPostResult] = useState<ProductionPostingResult | null>(null);

  // ── Recipe sync ────────────────────────────────────────────────────────────
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState(false);

  // ── Delete ─────────────────────────────────────────────────────────────────
  const [deleting, setDeleting] = useState(false);

  // ── Load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError(null);
    try {
      const [{ order: o, lines: ls }, locs, accts] = await Promise.all([
        productionOrderService.getProductionOrder(venueId, orderId),
        venueLocationService.getLocations(venueId),
        ownershipAccountService.listAccounts(venueId),
      ]);
      setOrder(o);
      setLines(ls);
      setLocations(locs);
      setAccounts(accts.data);

      recipeService.getRecipe(venueId, o.recipeId)
        .then(r => setRecipeName(r.recipe.name))
        .catch(() => {});
      inventoryItemService.getItem(venueId, o.outputInventoryItemId)
        .then(item => setOutputItemName(item.displayName))
        .catch(() => {});
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [orderId, venueId]);

  useEffect(() => { load(); }, [load]);

  // ── Load availability ──────────────────────────────────────────────────────
  const loadAvailability = useCallback(async () => {
    if (!orderId) return;
    setAvLoading(true);
    setAvError(null);
    try {
      const av = await productionOrderService.getProductionAvailability(venueId, orderId);
      setAvailability(av);
    } catch (err: any) {
      setAvError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to check availability');
    } finally {
      setAvLoading(false);
    }
  }, [orderId, venueId]);

  useEffect(() => {
    if (order?.status === 'DRAFT') loadAvailability();
  }, [order?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Header edit ────────────────────────────────────────────────────────────
  const openHeaderEdit = () => {
    if (!order) return;
    setEditQty(String(order.plannedQuantity));
    setEditLocId(order.locationId);
    setEditAcctId(order.outputOwnershipAccountId);
    setEditPlannedDate(isoToDateInput(order.plannedDate));
    setEditExpiryDate(isoToDateInput(order.outputExpiryDate));
    setEditLotNumber(order.outputLotNumber ?? '');
    setEditNotes(order.notes ?? '');
    setHeaderError(null);
    setHeaderEditing(true);
  };

  const saveHeader = async () => {
    if (!orderId) return;
    const qty = parseFloat(editQty);
    if (!qty || qty <= 0) { setHeaderError('Planned quantity must be greater than 0.'); return; }
    setHeaderSaving(true);
    setHeaderError(null);
    try {
      const { order: updated, lines: updatedLines } = await productionOrderService.updateProductionOrder(
        venueId,
        orderId,
        {
          plannedQuantity: qty,
          locationId: editLocId,
          outputOwnershipAccountId: editAcctId,
          plannedDate: editPlannedDate || null,
          outputExpiryDate: editExpiryDate || null,
          outputLotNumber: editLotNumber || null,
          notes: editNotes || null,
        },
      );
      setOrder(updated);
      setLines(updatedLines);
      setHeaderEditing(false);
      loadAvailability();
    } catch (err: any) {
      setHeaderError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to save changes');
    } finally {
      setHeaderSaving(false);
    }
  };

  // ── Line edit ──────────────────────────────────────────────────────────────
  const openLineEdit = async (line: ProductionOrderLine) => {
    setEditingLine(line);
    setLineFromLoc(line.fromLocationId);
    setLineOwnership(line.sourceOwnershipAccountId);
    setLineLotId(line.lotId ?? '');
    setLineError(null);
    setEditLineOpen(true);

    if (!lineLotsMap.has(line.inventoryItemId)) {
      setLineLotsLoading(true);
      try {
        const lotsRes = await inventoryLotService.getItemLots(venueId, line.inventoryItemId);
        setLineLotsMap(prev => {
          const next = new Map(prev);
          next.set(line.inventoryItemId, lotsRes);
          return next;
        });
      } catch {
        // non-blocking — lot select just won't be available
      } finally {
        setLineLotsLoading(false);
      }
    }
  };

  const saveLineEdit = async () => {
    if (!editingLine || !orderId) return;
    setLineSaving(true);
    setLineError(null);
    try {
      const payload: UpdateProductionOrderLinePayload = {
        fromLocationId: lineFromLoc,
        sourceOwnershipAccountId: lineOwnership,
        lotId: lineLotId || null,
      };
      const updatedLine = await productionOrderService.updateProductionOrderLine(
        venueId, orderId, editingLine._id, payload,
      );
      setLines(prev => prev.map(l => l._id === updatedLine._id ? updatedLine : l));
      setEditLineOpen(false);
      setEditingLine(null);
      loadAvailability();
    } catch (err: any) {
      setLineError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to save line');
    } finally {
      setLineSaving(false);
    }
  };

  // ── Posting ────────────────────────────────────────────────────────────────
  const executePost = async () => {
    if (!orderId) return;
    setPosting(true);
    setPostError(null);
    try {
      const result = await productionOrderService.postProductionOrder(venueId, orderId);
      setOrder(result.order);
      setPostResult(result);
    } catch (err: any) {
      setPostError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to post production order');
    } finally {
      setPosting(false);
    }
  };

  const handlePostClick = () => {
    if (availability?.recipeVersionDrift) {
      setDriftConfirmOpen(true);
    } else {
      if (!window.confirm('Post this production order? This will consume ingredients and create output stock. This cannot be undone.')) return;
      executePost();
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!orderId || !window.confirm('Delete this draft? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await productionOrderService.deleteProductionOrder(venueId, orderId);
      navigate(backPath);
    } catch (err: any) {
      setActionError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to delete order');
      setDeleting(false);
    }
  };

  // ── Recipe sync ────────────────────────────────────────────────────────────
  const handleSyncRecipe = async () => {
    if (!orderId) return;
    if (!window.confirm(
      'Sync ingredient lines from the current recipe?\n\n' +
      'Routing (from location, ownership, lot) will be preserved for unchanged ingredients. ' +
      'New ingredients get venue defaults. Removed ingredients are deleted.\n\n' +
      'This does not post the order.',
    )) return;
    setSyncing(true);
    setSyncError(null);
    setSyncSuccess(false);
    try {
      const { order: updated, lines: updatedLines } = await productionOrderService.syncProductionOrderRecipe(venueId, orderId);
      setOrder(updated);
      setLines(updatedLines);
      setSyncSuccess(true);
      loadAvailability();
    } catch (err: any) {
      setSyncError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to sync recipe');
    } finally {
      setSyncing(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const locName = (id: string) => locations.find(l => l._id === id)?.displayName ?? id;
  const acctName = (id: string) => accounts.find(a => a._id === id)?.name ?? id;
  const isDraft = order?.status === 'DRAFT';

  const currentLineLots = editingLine ? (lineLotsMap.get(editingLine.inventoryItemId) ?? null) : null;

  const availableLots = useMemo(() => {
    if (!currentLineLots?.lotTracked) return [];
    return currentLineLots.lots.filter(lot => {
      const qty = lot.locations.find(l => l.locationId === lineFromLoc)?.quantity ?? 0;
      return qty > 0;
    });
  }, [currentLineLots, lineFromLoc]);

  // ── Loading / error ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}>
          Production Orders
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!order) return null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Production Orders
        </Button>
        {isDraft && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
              onClick={handleDelete}
              disabled={deleting || posting}
              sx={{ textTransform: 'none' }}
            >
              Delete Draft
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={posting ? <CircularProgress size={14} color="inherit" /> : <PostIcon />}
              onClick={handlePostClick}
              disabled={posting || deleting}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {posting ? 'Posting…' : 'Post Production'}
            </Button>
          </Box>
        )}
      </Box>

      {actionError && <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>}
      {postError && <Alert severity="error" onClose={() => setPostError(null)}>{postError}</Alert>}

      {/* Post success */}
      {postResult && (
        <Alert severity="success" onClose={() => setPostResult(null)}>
          <strong>Production posted.</strong>{' '}
          Produced: <strong>{formatQty(postResult.outputMovement.quantity)}</strong> units.
          {postResult.outputLot?.lotNumber && (
            <> Output lot: <strong>{postResult.outputLot.lotNumber}</strong>.</>
          )}
          {postResult.availabilityWarning && (
            <> Note: {postResult.availabilityWarning}</>
          )}
        </Alert>
      )}

      {/* Recipe drift sync banner — shown when DRAFT + drift detected */}
      {isDraft && availability?.recipeVersionDrift && (
        <Alert
          severity="warning"
          icon={<WarnIcon />}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
              onClick={handleSyncRecipe}
              disabled={syncing || posting || deleting}
              sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {syncing ? 'Syncing…' : 'Sync from Current Recipe'}
            </Button>
          }
        >
          <strong>Recipe has changed since this order was created (v{order.recipeVersion}).</strong>{' '}
          Ingredient lines may be out of date. You can sync now, or post as-is after confirming the drift.
        </Alert>
      )}

      {syncError && <Alert severity="error" onClose={() => setSyncError(null)}>{syncError}</Alert>}

      {syncSuccess && (
        <Alert severity="success" onClose={() => setSyncSuccess(false)}>
          Ingredient lines synced from current recipe. Recipe version updated to v{order.recipeVersion}.
        </Alert>
      )}

      {/* Order header */}
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ProductionIcon sx={{ color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                Production Order
              </Typography>
              <Typography variant="h6" fontWeight={700} fontFamily="monospace" fontSize="1rem">
                {order._id.slice(-8).toUpperCase()}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <ProductionStatusBadge status={order.status} />
            {isDraft && !headerEditing && (
              <Button size="small" startIcon={<EditIcon />} onClick={openHeaderEdit} sx={{ textTransform: 'none' }}>
                Edit
              </Button>
            )}
          </Box>
        </Box>

        {headerError && <Alert severity="error" onClose={() => setHeaderError(null)} sx={{ mb: 2 }}>{headerError}</Alert>}

        {!headerEditing ? (
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[
              { label: 'Recipe', value: recipeName || order.recipeId.slice(-8) },
              { label: 'Recipe Version', value: `v${order.recipeVersion}` },
              { label: 'Output Item', value: outputItemName || order.outputInventoryItemId.slice(-8) },
              { label: 'Planned Qty', value: String(order.plannedQuantity) },
              { label: 'Output Location', value: locName(order.locationId) },
              { label: 'Output Ownership', value: acctName(order.outputOwnershipAccountId) },
              { label: 'Planned Date', value: fmtDate(order.plannedDate) },
              { label: 'Output Expiry', value: fmtDate(order.outputExpiryDate) },
              { label: 'Output Lot #', value: order.outputLotNumber || '(auto-generated)' },
              { label: order.postedAt ? 'Posted' : 'Created', value: fmtDate(order.postedAt ?? order.createdAt) },
            ].map(({ label, value }) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                  {label}
                </Typography>
                <Typography variant="body2" mt={0.25}>{value}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Planned Quantity *"
                size="small"
                type="number"
                value={editQty}
                onChange={e => setEditQty(e.target.value)}
                inputProps={{ min: 0.001, step: 'any' }}
                sx={{ width: 180 }}
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Output Location</InputLabel>
                <Select label="Output Location" value={editLocId} onChange={e => setEditLocId(e.target.value)}>
                  {locations.filter(l => l.active).map(l => (
                    <MenuItem key={l._id} value={l._id}>{l.displayName ?? l.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Output Ownership</InputLabel>
                <Select label="Output Ownership" value={editAcctId} onChange={e => setEditAcctId(e.target.value)}>
                  {accounts.filter(a => a.active).map(a => (
                    <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Planned Date" size="small" type="date"
                value={editPlannedDate} onChange={e => setEditPlannedDate(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ width: 200 }}
              />
              <TextField
                label="Output Expiry Date" size="small" type="date"
                value={editExpiryDate} onChange={e => setEditExpiryDate(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ width: 200 }}
              />
              <TextField
                label="Output Lot Number" size="small"
                value={editLotNumber} onChange={e => setEditLotNumber(e.target.value)}
                placeholder="Auto-generated if blank" sx={{ flex: 1, minWidth: 180 }}
              />
            </Box>
            <TextField
              label="Notes" size="small" multiline rows={2}
              value={editNotes} onChange={e => setEditNotes(e.target.value)} fullWidth
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" onClick={() => setHeaderEditing(false)} disabled={headerSaving} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={saveHeader}
                disabled={headerSaving}
                startIcon={headerSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {headerSaving ? 'Saving…' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        )}

        {order.notes && !headerEditing && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">{order.notes}</Typography>
          </>
        )}

        <Typography variant="caption" fontFamily="monospace" color="text.disabled" sx={{ display: 'block', mt: 2, fontSize: '0.68rem' }}>
          ID: {order._id}
        </Typography>
      </Paper>

      {/* Ingredient lines */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={600}>Ingredient Lines</Typography>
          <Typography variant="caption" color="text.secondary">{lines.length} {lines.length === 1 ? 'line' : 'lines'}</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Ingredient', 'Required', 'From Location', 'Source Ownership', 'Lot', 'Available', 'Status'].map(h => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}
                  >
                    {h}
                  </TableCell>
                ))}
                {isDraft && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isDraft ? 8 : 7}>
                    <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <Typography variant="body2">No ingredient lines on this order.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                lines.map(line => {
                  const av = avMap.get(line._id);
                  return (
                    <TableRow key={line._id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{line.snapshot.displayName}</Typography>
                        {(line.snapshot.sizeValue != null || line.snapshot.sizeUnit) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {line.snapshot.sizeValue}{line.snapshot.sizeUnit ? ` ${line.snapshot.sizeUnit}` : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {line.snapshot.recipeQuantity != null ? (
                          <>
                            <Typography variant="body2" fontWeight={500}>
                              {line.snapshot.recipeQuantity}{line.snapshot.recipeSizeUnit ? ` ${line.snapshot.recipeSizeUnit}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              ≈ {formatQty(line.requiredQuantity)} units
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" fontWeight={500}>{formatQty(line.requiredQuantity)}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{locName(line.fromLocationId)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{acctName(line.sourceOwnershipAccountId)}</Typography>
                      </TableCell>
                      <TableCell>
                        {line.lotId ? (
                          <Typography variant="caption" fontFamily="monospace" fontSize="0.72rem">{line.lotId.slice(-8)}</Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">FIFO</Typography>
                        )}
                        {av?.requiresLotSelection && !line.lotId && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>
                            Auto FIFO
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {avLoading ? (
                          <CircularProgress size={12} />
                        ) : av ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{ color: av.isAvailable ? 'success.main' : 'error.main', fontWeight: av.isAvailable ? 400 : 600 }}
                            >
                              {formatQty(av.availableQuantity)}
                            </Typography>
                            {!av.isAvailable && av.message && (
                              <Typography variant="caption" sx={{ color: 'warning.dark', display: 'block', fontSize: '0.68rem', mt: 0.25, lineHeight: 1.3 }}>
                                {av.message}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {!avLoading && av && (
                          av.isAvailable ? (
                            <Chip
                              label="OK"
                              size="small"
                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, color: '#1a7f37', bgcolor: '#dafbe1', border: 'none' }}
                            />
                          ) : (
                            <Chip
                              label={`−${formatQty(av.shortfallQuantity)}`}
                              size="small"
                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, color: '#cf222e', bgcolor: '#ffebe9', border: 'none' }}
                            />
                          )
                        )}
                        {!avLoading && av?.message && av.isAvailable && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', mt: 0.25 }}>
                            {av.message}
                          </Typography>
                        )}
                      </TableCell>
                      {isDraft && (
                        <TableCell align="right">
                          <Button size="small" onClick={() => openLineEdit(line)} sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, px: 1 }}>
                            Edit
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Availability panel — DRAFT only */}
      {isDraft && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>Availability Check</Typography>
            {availability && (
              <Chip
                label={availability.canProduce ? '✓ Can Produce' : '✗ Cannot Produce'}
                size="small"
                sx={{
                  fontWeight: 700, fontSize: '0.7rem',
                  color: availability.canProduce ? '#1a7f37' : '#cf222e',
                  bgcolor: availability.canProduce ? '#dafbe1' : '#ffebe9',
                  border: 'none', height: 24,
                }}
              />
            )}
            <Button
              size="small"
              startIcon={avLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
              onClick={loadAvailability}
              disabled={avLoading}
              sx={{ textTransform: 'none' }}
            >
              {avLoading ? 'Checking…' : 'Refresh'}
            </Button>
          </Box>

          {avError && (
            <Box sx={{ px: 3, pt: 2 }}>
              <Alert severity="error" onClose={() => setAvError(null)}>{avError}</Alert>
            </Box>
          )}

          {availability?.recipeVersionDrift && (
            <Box sx={{ px: 3, pt: 2 }}>
              <Alert severity="warning" icon={<WarnIcon />}>
                <strong>Recipe version mismatch.</strong>{' '}
                This order was created from recipe v{order.recipeVersion}, which has since been updated.
                You can still post using the ingredient lines as configured, but you will need to confirm.
              </Alert>
            </Box>
          )}

          {!availability && !avLoading && !avError && (
            <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Click Refresh to check ingredient availability.</Typography>
            </Box>
          )}

          {avLoading && !availability && (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Paper>
      )}

      {/* Line edit dialog */}
      <Dialog open={editLineOpen} onClose={() => { if (!lineSaving) { setEditLineOpen(false); setEditingLine(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Ingredient Line</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          {editingLine && (
            <Typography variant="body2" color="text.secondary">
              <strong>{editingLine.snapshot.displayName}</strong>
              {editingLine.snapshot.recipeQuantity != null ? (
                <> — {editingLine.snapshot.recipeQuantity}{editingLine.snapshot.recipeSizeUnit ? ` ${editingLine.snapshot.recipeSizeUnit}` : ''} <span style={{ opacity: 0.6 }}>(≈ {formatQty(editingLine.requiredQuantity)} stock units)</span></>
              ) : (
                <> — Required: {formatQty(editingLine.requiredQuantity)} stock units</>
              )}
            </Typography>
          )}

          {lineError && <Alert severity="error" onClose={() => setLineError(null)}>{lineError}</Alert>}

          <FormControl size="small" fullWidth>
            <InputLabel>From Location</InputLabel>
            <Select
              label="From Location"
              value={lineFromLoc}
              onChange={e => { setLineFromLoc(e.target.value); setLineLotId(''); }}
            >
              {locations.filter(l => l.active).map(l => (
                <MenuItem key={l._id} value={l._id}>{l.displayName ?? l.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Source Ownership</InputLabel>
            <Select label="Source Ownership" value={lineOwnership} onChange={e => setLineOwnership(e.target.value)}>
              {accounts.filter(a => a.active).map(a => (
                <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {lineLotsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">Loading lots…</Typography>
            </Box>
          )}

          {currentLineLots?.lotTracked && !lineLotsLoading && (
            <FormControl size="small" fullWidth>
              <InputLabel>Lot Override</InputLabel>
              <Select label="Lot Override" value={lineLotId} onChange={e => setLineLotId(e.target.value)}>
                <MenuItem value="">Auto (FIFO)</MenuItem>
                {availableLots.map(lot => {
                  const qty = lot.locations.find(l => l.locationId === lineFromLoc)?.quantity ?? 0;
                  return (
                    <MenuItem key={lot._id} value={lot._id}>
                      {lot.lotNumber} — {formatQty(qty)}
                    </MenuItem>
                  );
                })}
                {availableLots.length === 0 && lineFromLoc && (
                  <MenuItem disabled value="">No lots with stock at this location</MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setEditLineOpen(false); setEditingLine(null); }} disabled={lineSaving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveLineEdit}
            disabled={lineSaving}
            startIcon={lineSaving ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {lineSaving ? 'Saving…' : 'Save Line'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recipe version drift confirm dialog */}
      <Dialog
        open={driftConfirmOpen}
        onClose={() => { if (!posting) setDriftConfirmOpen(false); }}
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarnIcon color="warning" fontSize="small" />
          Confirm: Recipe Version Mismatch
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This order was created from recipe v{order.recipeVersion}, which has since been updated.
            Posting will deduct ingredients exactly as listed in the order lines.
          </Alert>
          <Typography variant="body2">
            Proceed with posting using the current ingredient lines as configured?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDriftConfirmOpen(false)} disabled={posting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => { setDriftConfirmOpen(false); executePost(); }}
            disabled={posting}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Post Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductionOrderDetail;
